import React, { useState } from "react";
import { sendContactEmail, addSquareCustomer } from "@repo/backend";
import { logConversionEvent } from "@repo/backend/firebase/analytics";

type Vehicle = "coupe" | "sedan" | "suv" | "truck" | "van";
type Condition = "light" | "moderate" | "severe";
type Service = "standard" | "deluxe" | "factory" | "ceramic";
type ServiceArea = "lima-oh" | "levittown-pa" | "jbmdl-nj";

const SERVICE_AREAS: { id: ServiceArea; label: string }[] = [
  { id: "lima-oh", label: "Lima, OH — 1 Hr Radius" },
  { id: "levittown-pa", label: "Levittown, PA — 2 Hr Radius" },
  { id: "jbmdl-nj", label: "JBMDL, NJ — 1 Hr Radius" },
];

const VEHICLES: { id: Vehicle; label: string; icon: string }[] = [
  { id: "coupe", label: "Coupe", icon: "🚗" },
  { id: "sedan", label: "Sedan", icon: "🚙" },
  { id: "suv", label: "SUV", icon: "🚐" },
  { id: "truck", label: "Truck", icon: "🛻" },
  { id: "van", label: "Van", icon: "🚌" },
];

const CONDITIONS: { id: Condition; label: string; sub: string }[] = [
  { id: "light", label: "Light", sub: "Clean interior, Minor Dust" },
  { id: "moderate", label: "Moderate", sub: "Brake dust, Light soiling" },
  { id: "severe", label: "Severe", sub: "Heavy grime, Heavy staining" },
];

const SERVICES: { id: Service; label: string; sub: string }[] = [
  { id: "standard", label: "Standard Detail", sub: "Complete interior & exterior cleaning" },
  { id: "deluxe", label: "Deluxe Detail", sub: "Deep clean + enhanced exterior protection" },
  { id: "factory", label: "Factory Detail", sub: "Top-to-bottom showroom restoration" },
  { id: "ceramic", label: "Ceramic Coating 5/yr", sub: "Years of durable protection — paint correction included" },
];

const ADDONS: Partial<Record<Service, { id: string; label: string; price: number }[]>> = {
  standard: [
    { id: "steam", label: "Steam Sanitization", price: 75 },
    { id: "engine", label: "Engine Bay Detail", price: 50 },
    { id: "headlight", label: "Headlight Restoration", price: 75 },
    { id: "leather", label: "Leather & Plastic Conditioning", price: 80 },
  ],
  deluxe: [
    { id: "engine", label: "Engine Bay Detail", price: 50 },
    { id: "headlight", label: "Headlight Restoration", price: 75 },
    { id: "ozone", label: "Ozone Odor Removal", price: 40 },
  ],
};

type ConditionPrices = Record<Condition, [number, number]>;
type PriceEntry = [number, number] | ConditionPrices;

const PRICES: Record<Vehicle, Record<Service, PriceEntry>> = {
  coupe: {
    standard: { light: [189, 225], moderate: [225, 270], severe: [265, 310] },
    deluxe:   { light: [200, 240], moderate: [240, 290], severe: [290, 330] },
    factory:  { light: [240, 300], moderate: [300, 375], severe: [390, 450] },
    ceramic:  [889, 1019],
  },
  sedan: {
    standard: { light: [199, 240], moderate: [240, 285], severe: [285, 330] },
    deluxe:   { light: [220, 260], moderate: [260, 310], severe: [310, 350] },
    factory:  { light: [280, 340], moderate: [340, 415], severe: [430, 500] },
    ceramic:  [1019, 1238],
  },
  suv: {
    standard: { light: [219, 260], moderate: [260, 310], severe: [310, 365] },
    deluxe:   { light: [240, 290], moderate: [290, 350], severe: [350, 410] },
    factory:  { light: [320, 390], moderate: [390, 475], severe: [490, 575] },
    ceramic:  [1238, 1398],
  },
  truck: {
    standard: { light: [229, 275], moderate: [275, 330], severe: [330, 390] },
    deluxe:   { light: [260, 310], moderate: [310, 375], severe: [375, 440] },
    factory:  { light: [350, 425], moderate: [425, 525], severe: [540, 650] },
    ceramic:  [1398, 1698],
  },
  van: {
    standard: { light: [249, 300], moderate: [300, 360], severe: [360, 425] },
    deluxe:   { light: [280, 340], moderate: [340, 410], severe: [410, 490] },
    factory:  { light: [400, 500], moderate: [500, 600], severe: [625, 750] },
    ceramic:  [1400, 1800],
  },
};

function calcBase(service: Service, vehicle: Vehicle, condition: Condition): [number, number] {
  const p = PRICES[vehicle][service];
  if (Array.isArray(p)) return p as [number, number];
  return (p as ConditionPrices)[condition];
}

const STEP_LABELS = ["Vehicle", "Condition", "Service", "Add-Ons", "Your Quote"];
const ESTIMATE_STEP = 4;

const BLUE = "#1E5EFF";
const BTN: React.CSSProperties = { fontFamily: "var(--font-display)", borderRadius: 0 };

function OptionButton({ selected, onClick, children }: { selected: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left px-5 py-4 border transition-all duration-200 font-bold uppercase text-sm"
      style={{
        ...BTN,
        letterSpacing: "0.08em",
        background: selected ? BLUE : "#0F0F0F",
        borderColor: selected ? BLUE : "#252525",
        color: selected ? "#fff" : "#F2F2F0",
        boxShadow: selected ? "0 0 24px rgba(30,94,255,0.25)" : "none",
      }}
    >
      {children}
    </button>
  );
}

function Field({ label, type = "text", value, onChange, placeholder }: {
  label: string; type?: string; value: string; onChange: (v: string) => void; placeholder: string;
}) {
  return (
    <div>
      <label
        className="block mb-2 font-bold uppercase text-xs tracking-widest"
        style={{ color: "#808080" }}
      >
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-transparent border px-4 py-3 text-sm outline-none transition-colors"
        style={{
          borderColor: "#252525",
          color: "#F2F2F0",
          borderRadius: 0,
          caretColor: BLUE,
          fontFamily: "var(--font-sans)",
        }}
        onFocus={e => (e.currentTarget.style.borderColor = BLUE)}
        onBlur={e => (e.currentTarget.style.borderColor = "#252525")}
      />
    </div>
  );
}

function SelectField<T extends string>({ label, value, onChange, options, placeholder }: {
  label: string;
  value: T | "";
  onChange: (v: T) => void;
  options: { id: T; label: string }[];
  placeholder: string;
}) {
  return (
    <div>
      <label
        className="block mb-2 font-bold uppercase text-xs tracking-widest"
        style={{ color: "#808080" }}
      >
        {label}
      </label>
      <select
        value={value}
        onChange={e => onChange(e.target.value as T)}
        className="w-full bg-transparent border px-4 py-3 text-sm outline-none transition-colors"
        style={{
          borderColor: "#252525",
          color: value ? "#F2F2F0" : "#555",
          borderRadius: 0,
          fontFamily: "var(--font-sans)",
        }}
        onFocus={e => (e.currentTarget.style.borderColor = BLUE)}
        onBlur={e => (e.currentTarget.style.borderColor = "#252525")}
      >
        <option value="" disabled style={{ color: "#555" }}>{placeholder}</option>
        {options.map(o => (
          <option key={o.id} value={o.id} style={{ color: "#F2F2F0", background: "#0F0F0F" }}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

export function QuoteCalculator() {
  const [step, setStep] = useState(0);
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [condition, setCondition] = useState<Condition | null>(null);
  const [service, setService] = useState<Service | null>(null);
  const [selectedAddons, setSelectedAddons] = useState<Set<string>>(new Set());

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [serviceArea, setServiceArea] = useState<ServiceArea | "">("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const hasAddons = service === "standard" || service === "deluxe";
  const availableAddons = service ? (ADDONS[service] ?? []) : [];

  const reset = () => {
    setStep(0); setVehicle(null); setCondition(null); setService(null);
    setSelectedAddons(new Set());
    setName(""); setEmail(""); setPhone(""); setServiceArea(""); setSubmitted(false);
  };

  const toggleAddon = (id: string) => {
    setSelectedAddons(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const addonTotal = availableAddons
    .filter(a => selectedAddons.has(a.id))
    .reduce((sum, a) => sum + a.price, 0);

  const baseRange = vehicle && condition && service ? calcBase(service, vehicle, condition) : null;
  const range: [number, number] | null = baseRange
    ? [baseRange[0] + addonTotal, baseRange[1] + addonTotal]
    : null;

  const vehicleLabel = VEHICLES.find(v => v.id === vehicle)?.label ?? "";
  const serviceLabel = SERVICES.find(s => s.id === service)?.label ?? "";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!vehicle || !condition || !service || !baseRange) return;
    setSubmitting(true);
    try {
      const conditionLabel = CONDITIONS.find(c => c.id === condition)?.label ?? condition;
      const serviceAreaLabel = SERVICE_AREAS.find(a => a.id === serviceArea)?.label ?? serviceArea;
      const selectedAddonLabels = availableAddons.filter(a => selectedAddons.has(a.id)).map(a => a.label);
      const estimateMin = baseRange[0] + addonTotal;
      const estimateMax = baseRange[1] + addonTotal;
      const quoteNote = [
        `Vehicle: ${vehicleLabel}`,
        `Service: ${serviceLabel}`,
        `Condition: ${conditionLabel}`,
        selectedAddonLabels.length ? `Add-Ons: ${selectedAddonLabels.join(", ")}` : null,
        `Service Area: ${serviceAreaLabel}`,
        `Estimate: $${estimateMin.toLocaleString()}–$${estimateMax.toLocaleString()}`,
      ].filter(Boolean).join(" | ");

      await Promise.all([
        sendContactEmail({
          clientId: "bizzydetails",
          customerName: name,
          customerEmail: email,
          subject: `New Quote Request — ${name} (${vehicleLabel})`,
          fields: [
            { label: "Phone", value: phone },
            { label: "Service Area", value: serviceAreaLabel },
            { label: "Vehicle", value: vehicleLabel },
            { label: "Service", value: serviceLabel },
            { label: "Condition", value: conditionLabel },
            ...(selectedAddonLabels.length ? [{ label: "Add-Ons", value: selectedAddonLabels.join(", ") }] : []),
            { label: "Estimate", value: `$${estimateMin.toLocaleString()}–$${estimateMax.toLocaleString()}` },
          ],
          confirmationMessage: `Thanks for reaching out to Bizzy Details! We've received your quote request for your ${vehicleLabel} and will be in touch within 24 hours to confirm your appointment.`,
        }),
        addSquareCustomer({
          clientId: "bizzydetails",
          customerName: name,
          customerEmail: email,
          customerPhone: phone,
          note: quoteNote,
        }).catch(() => {}),
      ]);
      logConversionEvent("generate_lead", {
        form_type: "contact",
        value: (estimateMin + estimateMax) / 2,
        currency: "USD",
      });
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const eyebrow = (
    <div className="flex items-center gap-3 mb-4" style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.3em", textTransform: "uppercase", color: BLUE }}>
      <span style={{ width: 24, height: 2, background: BLUE, display: "inline-block", flexShrink: 0 }} />
      Instant Quote + Contact
    </div>
  );

  return (
    <section className="py-28 border-b" style={{ background: "#0A0A0A", borderColor: "#1a1a1a" }} id="quote">
      <div className="max-w-3xl mx-auto px-6">

        {/* Header */}
        <div className="mb-12">
          {eyebrow}
          <h2
            className="font-black italic uppercase"
            style={{ fontFamily: "var(--font-display)", fontSize: "clamp(2.5rem,5vw,4.5rem)", lineHeight: 0.9, color: "#F2F2F0" }}
          >
            What Are<br />We Working With?
          </h2>
        </div>

        {/* Step indicator */}
        <div className="flex gap-2 mb-10">
          {STEP_LABELS.map((label, i) => (
            <div key={label} className="flex items-center gap-2 flex-1">
              <div
                className="flex items-center justify-center w-6 h-6 font-black text-xs flex-shrink-0"
                style={{ ...BTN, background: i <= step ? BLUE : "#1a1a1a", color: i <= step ? "#fff" : "#555" }}
              >
                {i + 1}
              </div>
              <span className="text-xs font-bold uppercase hidden sm:block" style={{ letterSpacing: "0.12em", color: i <= step ? "#F2F2F0" : "#555" }}>
                {label}
              </span>
              {i < STEP_LABELS.length - 1 && (
                <div className="flex-1 h-px ml-1" style={{ background: i < step ? BLUE : "#252525" }} />
              )}
            </div>
          ))}
        </div>

        {/* Step 0 — Vehicle */}
        {step === 0 && (
          <div>
            <p className="mb-6 font-bold uppercase text-xs tracking-widest" style={{ color: "#808080" }}>What are we detailing?</p>
            <div className="grid grid-cols-5 gap-2">
              {VEHICLES.map(v => (
                <OptionButton key={v.id} selected={vehicle === v.id} onClick={() => setVehicle(v.id)}>
                  <div className="text-2xl mb-2">{v.icon}</div>
                  <div>{v.label}</div>
                </OptionButton>
              ))}
            </div>
            <button disabled={!vehicle} onClick={() => setStep(1)}
              className="mt-8 px-10 py-4 font-black italic uppercase tracking-widest text-sm transition-opacity disabled:opacity-30"
              style={{ ...BTN, background: BLUE, color: "#fff" }}
            >
              Next →
            </button>
          </div>
        )}

        {/* Step 1 — Condition */}
        {step === 1 && (
          <div>
            <p className="mb-6 font-bold uppercase text-xs tracking-widest" style={{ color: "#808080" }}>How's it looking right now?</p>
            <div className="flex flex-col gap-2">
              {CONDITIONS.map(c => (
                <OptionButton key={c.id} selected={condition === c.id} onClick={() => setCondition(c.id)}>
                  <span>{c.label}</span>
                  <span className="ml-3 font-normal normal-case text-xs opacity-60" style={{ fontFamily: "var(--font-sans)", letterSpacing: 0 }}>{c.sub}</span>
                </OptionButton>
              ))}
            </div>
            <div className="flex gap-3 mt-8">
              <button onClick={() => setStep(0)} className="px-6 py-4 border text-sm font-bold uppercase tracking-widest" style={{ borderColor: "#252525", color: "#808080", borderRadius: 0 }}>← Back</button>
              <button disabled={!condition} onClick={() => setStep(2)}
                className="px-10 py-4 font-black italic uppercase tracking-widest text-sm disabled:opacity-30"
                style={{ ...BTN, background: BLUE, color: "#fff" }}
              >
                Next →
              </button>
            </div>
          </div>
        )}

        {/* Step 2 — Service */}
        {step === 2 && (
          <div>
            <p className="mb-6 font-bold uppercase text-xs tracking-widest" style={{ color: "#808080" }}>What service are you after?</p>
            <div className="flex flex-col gap-2">
              {SERVICES.map(s => (
                <OptionButton key={s.id} selected={service === s.id} onClick={() => setService(s.id)}>
                  <span>{s.label}</span>
                  <span className="ml-3 font-normal normal-case text-xs opacity-60" style={{ fontFamily: "var(--font-sans)", letterSpacing: 0 }}>{s.sub}</span>
                </OptionButton>
              ))}
            </div>
            <div className="flex gap-3 mt-8">
              <button onClick={() => setStep(1)} className="px-6 py-4 border text-sm font-bold uppercase tracking-widest" style={{ borderColor: "#252525", color: "#808080", borderRadius: 0 }}>← Back</button>
              <button disabled={!service} onClick={() => {
                setSelectedAddons(new Set());
                setStep(service === "factory" || service === "ceramic" ? ESTIMATE_STEP : 3);
              }}
                className="px-10 py-4 font-black italic uppercase tracking-widest text-sm disabled:opacity-30"
                style={{ ...BTN, background: BLUE, color: "#fff" }}
              >
                {service === "factory" || service === "ceramic" ? "See Estimate →" : "Next →"}
              </button>
            </div>
          </div>
        )}

        {/* Step 3 — Add-ons */}
        {step === 3 && (
          <div>
            <p className="mb-6 font-bold uppercase text-xs tracking-widest" style={{ color: "#808080" }}>Want to add anything?</p>
            <div className="flex flex-col gap-2">
              {availableAddons.map(addon => (
                <OptionButton key={addon.id} selected={selectedAddons.has(addon.id)} onClick={() => toggleAddon(addon.id)}>
                  <span>{addon.label}</span>
                  <span className="ml-3 font-normal normal-case text-xs opacity-60" style={{ fontFamily: "var(--font-sans)", letterSpacing: 0 }}>+${addon.price}</span>
                </OptionButton>
              ))}
            </div>
            {addonTotal > 0 && (
              <p className="mt-4 text-xs font-bold uppercase tracking-widest" style={{ color: BLUE }}>
                Add-ons selected: +${addonTotal}
              </p>
            )}
            <div className="flex gap-3 mt-8">
              <button onClick={() => setStep(2)} className="px-6 py-4 border text-sm font-bold uppercase tracking-widest" style={{ borderColor: "#252525", color: "#808080", borderRadius: 0 }}>← Back</button>
              <button onClick={() => setStep(ESTIMATE_STEP)}
                className="px-10 py-4 font-black italic uppercase tracking-widest text-sm"
                style={{ ...BTN, background: BLUE, color: "#fff" }}
              >
                See Estimate →
              </button>
            </div>
          </div>
        )}

        {/* Step 4 — Estimate + Contact */}
        {step === ESTIMATE_STEP && range && !submitted && (
          <div>
            <div className="mb-10 pb-10" style={{ borderBottom: "1px solid #1a1a1a" }}>
              <p className="mb-1 font-bold uppercase text-xs tracking-widest" style={{ color: "#808080" }}>Ballpark estimate</p>
              <div
                className="font-black italic"
                style={{ fontFamily: "var(--font-display)", fontSize: "clamp(3rem,8vw,5.5rem)", lineHeight: 1, color: "#F2F2F0" }}
              >
                ~${range[0].toLocaleString()}<span style={{ color: BLUE }}>–</span>${range[1].toLocaleString()}
              </div>
              {addonTotal > 0 && (
                <p className="mt-2 text-xs font-bold uppercase tracking-widest" style={{ color: BLUE }}>
                  Includes ${addonTotal} in add-ons
                </p>
              )}
              <p className="mt-3 text-sm" style={{ color: "#555", lineHeight: 1.7 }}>
                Based on a <span style={{ color: "#F2F2F0" }}>{vehicleLabel}</span> needing <span style={{ color: "#F2F2F0" }}>{serviceLabel}</span>. Final price confirmed on-site after we see the vehicle — no surprises.
              </p>
            </div>

            <p className="mb-6 font-bold uppercase text-xs tracking-widest" style={{ color: "#808080" }}>Lock in your spot — we'll reach out within 24 hrs</p>
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <Field label="Name" value={name} onChange={setName} placeholder="First & last name" />
              <Field label="Email" type="email" value={email} onChange={setEmail} placeholder="you@email.com" />
              <Field label="Phone" type="tel" value={phone} onChange={setPhone} placeholder="(555) 000-0000" />
              <SelectField
                label="Service Area"
                value={serviceArea}
                onChange={setServiceArea}
                options={SERVICE_AREAS}
                placeholder="Select your area"
              />

              <div className="flex gap-3 mt-4">
                <button type="button" onClick={() => setStep(hasAddons ? 3 : 2)} className="px-6 py-4 border text-sm font-bold uppercase tracking-widest" style={{ borderColor: "#252525", color: "#808080", borderRadius: 0 }}>← Back</button>
                <button
                  type="submit"
                  disabled={submitting || !name || !email || !phone || !serviceArea}
                  className="flex-1 py-4 font-black italic uppercase tracking-widest text-sm disabled:opacity-40 transition-opacity"
                  style={{ ...BTN, background: BLUE, color: "#fff", boxShadow: "0 0 32px rgba(30,94,255,0.35)" }}
                >
                  {submitting ? "Sending..." : "Request My Quote →"}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Submitted */}
        {submitted && (
          <div className="py-12 text-center">
            <div style={{ color: BLUE, marginBottom: 20 }}>
              <svg width="48" height="48" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} style={{ margin: "0 auto" }}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3
              className="font-black italic uppercase mb-3"
              style={{ fontFamily: "var(--font-display)", fontSize: "clamp(1.8rem,4vw,3rem)", lineHeight: 0.95, color: "#F2F2F0" }}
            >
              You're On<br />The List.
            </h3>
            <p style={{ color: "#808080", fontSize: "0.95rem", lineHeight: 1.7, marginBottom: 32 }}>
              We've got your quote request for the <span style={{ color: "#F2F2F0" }}>{vehicleLabel} {serviceLabel}</span>.<br />
              Expect a call or text from us within 24 hours.
            </p>
            <button
              onClick={reset}
              className="px-8 py-4 border text-sm font-bold uppercase tracking-widest"
              style={{ borderColor: "#252525", color: "#808080", borderRadius: 0 }}
            >
              Start a New Quote
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
